import React from 'react';

import { Columns, DataGridRow, RowStatus } from 'components/DataGrid';
import { BoundingBox, FieldGroupColumn, PredictionColumn } from 'models/response';
import { UserDate, UserDecimalSep } from 'models/user';
import store from 'state/store';
import actions from 'state/actions';
import CellPredictionDropdown from './CellPredictionDropdown';
import CellPredictionTextInput from './CellPredictionTextInput';
import CellPredictionBooleanInput from './CellPredictionBooleanInput';
import utils from 'utils';
import intlHelper from 'utils/helpers/intl.helper';

const onFocus = (cell?: { pageNumber: number; boundingBox: BoundingBox; content: string }) => {
  try {
    store.dispatch(actions.documents.setActiveBox(null));
    if (cell) {
      const { pageNumber, boundingBox } = cell;
      const { x1, x2, y1, y2 } = boundingBox;
      if (x1 > 0 && x2 > 0 && y1 > 0 && y2 > 0 && pageNumber > -1) {
        store.dispatch(
          actions.documents.setActiveBox({
            pageNumber,
            x1,
            y1,
            x2,
            y2,
          }),
        );
      }
    }
  } catch (err) {
    console.error(err);
  }
};

// Function that calculates options based on rowNumber and idColumn
const calculateOptions = (
  rowId: string,
  columnCode: string,
  predictionsValues: PredictionColumn[],
  isTypeDate: boolean,
  isNumberType: boolean,
  isSelectOptionType: boolean,
  decimalSep: UserDecimalSep,
  decimalScale: number,
  hTableValues: {
    code: string;
    description: string;
    sign?: 1 | -1 | undefined;
  }[],
) => {
  // Find the prediction row that matches rowId and columnCode
  const predictionRow = predictionsValues.find((prediction) =>
    Object.values(prediction).some((p) => p.rowNumber === parseInt(rowId) && p.idColumn === parseInt(columnCode)),
  );

  if (!predictionRow) return [];

  const predictionContentList = predictionRow[columnCode]?.predictionContentList || [];

  // Map through prediction content list to generate options
  return predictionContentList.map((prediction: { content: string; confidence: number }) => {
    let formattedContent;
    if (isTypeDate) {
      formattedContent = utils.date.convertSelectedDateFromPrediction(prediction.content) || '';
    } else if (isNumberType) {
      formattedContent = intlHelper.formatNumber(parseFloat(prediction.content) || 0, decimalScale, decimalSep);
    } else if (isSelectOptionType) {
      const fromTableValues = hTableValues.find((el) => el.code === prediction.content);
      if (fromTableValues) {
        return {
          label: `${fromTableValues.code} - ${fromTableValues.description}`,
          value: fromTableValues.code,
          confidence: prediction.confidence,
        };
      }
      formattedContent = prediction.content;
    } else {
      formattedContent = prediction.content;
    }

    return {
      label: formattedContent,
      value: formattedContent,
      confidence: prediction.confidence,
    };
  });
};

export const makeDataPredictionGridColumns = (
  cols: FieldGroupColumn[],
  predictionsValues: PredictionColumn[],
  predictionsValuesBody: DataGridRow[],
  userPreference: { decimalSep: UserDecimalSep; dateFormat: UserDate },
  decimalScale: number,
  rows: DataGridRow[],
  gridRef?: any,
) => {
  const idColumn: Columns = {
    title: 'ID',
    type: 'text',
    value: (row: DataGridRow) => {
      const rowIndex = rows.indexOf(row);
      return <span>{rowIndex + 1}</span>;
    },
    id: 'id',
    width: 50,
  };

  const generatedColumns = cols?.map((col): Columns => {
    const isDateType = col.type === 1;
    const isBooleanType = col.dataType === 0 && col.type === 4;
    const isNumberType = col.type === 3;
    const columnTitle = col.translate || col.title;
    const isSelectOptionType = col.dataType === 3;

    return {
      title: columnTitle,
      type: isDateType ? 'date' : isNumberType ? 'number' : 'text',
      value: (row: DataGridRow, { focus }: RowStatus) => {
        const options = calculateOptions(
          row.id,
          col.code.toString(),
          predictionsValues,
          isDateType,
          isNumberType,
          isSelectOptionType,
          userPreference.decimalSep,
          decimalScale,
          col.hTableValues,
        );
        const hasMultipleOptions = options.length > 1;
        const isSingleOption = options.length === 1;

        // Render appropriate cell based on conditions
        if (hasMultipleOptions) {
          return isBooleanType ? (
            <CellPredictionBooleanInput
              focus={focus}
              row={row}
              code={col.code}
              predictionsValuesBody={predictionsValuesBody}
            />
          ) : (
            <CellPredictionDropdown
              focus={focus}
              onFocus={onFocus}
              row={row}
              code={col.code}
              options={options}
              gridRef={gridRef}
              disabled={false}
              predictionsValuesBody={predictionsValuesBody}
            />
          );
        } else if (isSingleOption) {
          return isBooleanType ? (
            <CellPredictionBooleanInput
              focus={focus}
              row={row}
              code={col.code}
              predictionsValuesBody={predictionsValuesBody}
              disabled={true}
            />
          ) : (
            <CellPredictionTextInput
              row={row}
              code={col.code}
              predictionsValuesBody={predictionsValuesBody}
              dataType={col.dataType}
              hTableValues={col.hTableValues}
            />
          );
        }

        // Default cell rendering
        return <CellPredictionTextInput row={row} code={col.code} predictionsValuesBody={predictionsValuesBody} />;
      },
      id: col.code,
      dateFormat: userPreference.dateFormat,
      userSeparator: userPreference.decimalSep,
    };
  });

  return [idColumn, ...(generatedColumns || [])];
};
